import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

const STORAGE_KEY = 'rosarium:wishlist:v1';
const STORAGE_VERSION = 1;
const ACCOUNT_STATE_EVENT = 'rosarium:wishlist-account-state';
const PRODUCT_ID_PATTERN = /^[1-9]\d{0,19}$/;
const IMPORT_PAYLOAD_PATTERN = /^[1-9]\d{0,19}(?:\.[1-9]\d{0,19})*$/;
const MAX_IMPORT_PAYLOAD_LENGTH = 839;
const ACCOUNT_ACTION_QUERY_KEY = 'rosarium_wishlist_action';
const ACCOUNT_ITEMS_QUERY_KEY = 'rosarium_wishlist_items';

const config = readConfig();
const runtime = {
  signedIn: config.signedIn,
  localItems: [],
  account: {
    count: 0,
    items: [],
    productIds: new Set(),
    pageUrl: config.pageUrl,
  },
  storageStatus: 'available',
};

runtime.localItems = readLocalItems();

class RosariumWishlistAction extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.handleClick);
    this.observer ??= new MutationObserver(() => this.queueSync());
    this.observer.observe(this, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: [
        'aria-label',
        'data-label-add',
        'data-label-remove',
        'data-message-added',
        'data-message-removed',
        'data-product-handle',
        'data-product-id',
        'data-product-image-alt',
        'data-product-image-url',
        'data-product-price',
        'data-product-title',
      ],
    });
    this.queueSync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.observer?.disconnect();
  }

  handleClick = (event) => {
    const button = event.target.closest('[data-wishlist-action-link]');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    event.preventDefault();
    const item = itemFromElement(this);
    if (!item) return;

    if (
      runtime.account.productIds.has(item.id) &&
      !runtime.localItems.some((localItem) => localItem.id === item.id)
    ) {
      const removeUrl = buildAccountRoute('remove', [item.id]);
      if (removeUrl) window.location.assign(removeUrl);
      return;
    }

    toggleLocalItem(item, {
      addedMessage: this.dataset.messageAdded,
      removedMessage: this.dataset.messageRemoved,
    });
  };

  queueSync() {
    if (this.syncQueued) return;
    this.syncQueued = true;

    queueMicrotask(() => {
      this.syncQueued = false;
      if (this.isConnected) this.sync();
    });
  }

  sync() {
    const productId = normalizeProductId(this.dataset.productId);
    const button = this.querySelector('[data-wishlist-action-link]');
    if (!(button instanceof HTMLButtonElement)) return;

    if (!productId) {
      button.disabled = true;
      return;
    }

    const localItem = runtime.localItems.find((item) => item.id === productId);
    const isSaved = Boolean(localItem) || runtime.account.productIds.has(productId);
    const nextLabel = isSaved ? this.dataset.labelRemove : this.dataset.labelAdd;

    button.disabled = false;
    button.setAttribute('aria-pressed', String(isSaved));
    button.classList.toggle('rosarium-wishlist-action--saved', isSaved);
    this.classList.toggle('rosarium-wishlist-action-host--saved', isSaved);
    this.toggleAttribute('data-account-saved', runtime.account.productIds.has(productId));

    if (nextLabel && button.getAttribute('aria-label') !== nextLabel) {
      button.setAttribute('aria-label', nextLabel);
    }

    if (localItem) refreshLocalSnapshot(localItem, itemFromElement(this));
  }
}

class RosariumWishlistHeader extends HTMLElement {
  connectedCallback() {
    this.observer ??= new MutationObserver(() => this.queueSync());
    this.observer.observe(this, { childList: true, subtree: true });
    this.queueSync();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  queueSync() {
    if (this.syncQueued) return;
    this.syncQueued = true;

    queueMicrotask(() => {
      this.syncQueued = false;
      if (this.isConnected) this.sync();
    });
  }

  sync() {
    const button = this.querySelector('[data-wishlist-header-link]');
    if (!(button instanceof HTMLButtonElement)) return;

    const count = combinedCount();
    const displayCount = count > 99 ? '99+' : String(count);
    const label = count === 0
      ? this.dataset.labelZero
      : count === 1
        ? this.dataset.labelOne
        : (this.dataset.labelMany || '').replace('__COUNT__', String(count));

    this.dataset.wishlistCount = String(count);
    button.dataset.wishlistCount = String(count);
    button.dataset.wishlistCountDisplay = displayCount;
    button.classList.toggle('rosarium-wishlist-header__link--has-items', count > 0);
    if (label) button.setAttribute('aria-label', label);
  }
}

class RosariumWishlistDrawer extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.handleDrawerClick);
    this.addEventListener(DialogCloseEvent.eventName, this.handleDialogClose);
    this.render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.handleDrawerClick);
    this.removeEventListener(DialogCloseEvent.eventName, this.handleDialogClose);
  }

  open(event) {
    this.opener = event?.target instanceof HTMLElement
      ? event.target
      : document.activeElement;
    this.render();
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }

  handleDialogClose = () => {
    const opener = this.opener;
    this.opener = null;
    if (!(opener instanceof HTMLElement) || !opener.isConnected) return;
    queueMicrotask(() => opener.focus({ preventScroll: true }));
  };

  handleDrawerClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const removeButton = event.target.closest('[data-wishlist-remove-local]');
    if (!(removeButton instanceof HTMLButtonElement) || !this.contains(removeButton)) return;
    event.stopImmediatePropagation();

    const productId = normalizeProductId(removeButton.dataset.productId);
    if (!productId) return;

    const item = runtime.localItems.find((localItem) => localItem.id === productId);
    if (!item) return;

    const row = removeButton.closest('.rosarium-wishlist-drawer__item');
    const list = row?.parentElement;
    const rowIndex = list ? [...list.children].indexOf(row) : -1;
    const productTitle = item.title || '';
    const message = removeButton.dataset.messageTemplate?.replace('__PRODUCT__', productTitle);
    removeLocalItem(productId, message);

    queueMicrotask(() => {
      const nextRows = this.querySelectorAll('.rosarium-wishlist-drawer__item');
      const nextRow = nextRows[Math.min(Math.max(rowIndex, 0), nextRows.length - 1)];
      const nextTarget = nextRow?.querySelector('.rosarium-wishlist-drawer__remove') ||
        this.querySelector('[data-wishlist-account-action]');
      if (nextTarget instanceof HTMLElement && !nextTarget.hidden) {
        nextTarget.focus({ preventScroll: true });
      }
    });
  };

  render() {
    const list = this.querySelector('[data-wishlist-list]');
    const empty = this.querySelector('[data-wishlist-empty]');
    const note = this.querySelector('[data-wishlist-note]');
    const accountAction = this.querySelector('[data-wishlist-account-action]');
    if (!(list instanceof HTMLUListElement) || !(empty instanceof HTMLElement)) return;

    const items = combinedItems();
    list.replaceChildren(...items.map((entry) => createDrawerItem(entry)));
    list.hidden = items.length === 0;
    empty.hidden = items.length > 0;

    const localCount = runtime.localItems.length;
    const accountCount = Math.max(runtime.account.count, runtime.account.productIds.size);

    if (note instanceof HTMLElement) {
      note.textContent = localCount > 0
        ? runtime.signedIn
          ? config.messages.pendingNote
          : config.messages.localNote
        : accountCount > 0
          ? config.messages.accountNote
          : config.messages.localNote;
    }

    if (accountAction instanceof HTMLAnchorElement) {
      const nextUrl = localCount > 0
        ? buildAccountRoute('import', runtime.localItems.map((item) => item.id))
        : buildAccountRoute();

      accountAction.hidden = !nextUrl;
      if (nextUrl) accountAction.href = nextUrl;
      accountAction.textContent = localCount > 0
        ? runtime.signedIn
          ? config.messages.syncCta
          : config.messages.signInCta
        : runtime.signedIn
          ? config.messages.openAccountCta
          : config.messages.signInOnlyCta;
    }
  }
}

function readConfig() {
  const fallback = {
    signedIn: false,
    pageUrl: '',
    rootUrl: '/',
    maxLocalItems: 40,
    messages: {},
  };

  try {
    const element = document.getElementById('rosarium-wishlist-config');
    const parsed = JSON.parse(element?.textContent || '{}');
    const messages = Object.fromEntries(
      Object.entries(parsed.messages || {}).map(([key, value]) => [
        key,
        typeof value === 'string' ? value.slice(0, 500) : '',
      ]),
    );

    return Object.freeze({
      signedIn: parsed.signedIn === true,
      pageUrl: safeHttpsUrl(parsed.pageUrl),
      rootUrl: normalizeRootUrl(parsed.rootUrl),
      maxLocalItems: Math.min(40, Math.max(1, Number(parsed.maxLocalItems) || 40)),
      messages: Object.freeze(messages),
    });
  } catch {
    return Object.freeze(fallback);
  }
}

function readLocalItems() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (parsed?.version !== STORAGE_VERSION || !Array.isArray(parsed.items)) {
      localStorage.removeItem(STORAGE_KEY);
      return [];
    }

    return uniqueItems(parsed.items.map(normalizeItem).filter(Boolean)).slice(0, config.maxLocalItems);
  } catch {
    try {
      localStorage.removeItem(STORAGE_KEY);
      runtime.storageStatus = 'available';
    } catch {
      runtime.storageStatus = 'unavailable';
    }
    return [];
  }
}

function persistLocalItems() {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ version: STORAGE_VERSION, items: runtime.localItems }),
    );
    runtime.storageStatus = 'available';
    return true;
  } catch {
    runtime.storageStatus = 'memory_only';
    return false;
  }
}

function itemFromElement(element) {
  return normalizeItem({
    id: element.dataset.productId,
    handle: element.dataset.productHandle,
    title: element.dataset.productTitle,
    price: element.dataset.productPrice,
    imageUrl: element.dataset.productImageUrl,
    imageAlt: element.dataset.productImageAlt,
    savedAt: Date.now(),
  });
}

function normalizeItem(value) {
  const id = normalizeProductId(value?.id);
  const handle = normalizeHandle(value?.handle);
  const title = normalizeText(value?.title, 240);
  if (!id || !handle || !title) return null;

  return Object.freeze({
    id,
    handle,
    title,
    price: normalizeText(value?.price, 80),
    imageUrl: normalizeImageUrl(value?.imageUrl),
    imageAlt: normalizeText(value?.imageAlt, 240) || title,
    savedAt: Number.isSafeInteger(value?.savedAt) && value.savedAt > 0
      ? value.savedAt
      : 0,
  });
}

function normalizeProductId(value) {
  const id = String(value || '');
  return PRODUCT_ID_PATTERN.test(id) ? id : '';
}

function normalizeHandle(value) {
  const handle = String(value || '').trim();
  return handle.length <= 255 && /^[^/?#\s]+$/.test(handle) ? handle : '';
}

function normalizeText(value, maxLength) {
  return typeof value === 'string' ? value.trim().slice(0, maxLength) : '';
}

function normalizeImageUrl(value) {
  if (typeof value !== 'string' || !value.trim()) return '';

  try {
    const url = new URL(value, document.baseURI);
    return url.protocol === 'https:' ? url.href : '';
  } catch {
    return '';
  }
}

function normalizeRootUrl(value) {
  const root = typeof value === 'string' && value.startsWith('/') ? value : '/';
  return root.endsWith('/') ? root : `${root}/`;
}

function safeHttpsUrl(value) {
  if (typeof value !== 'string' || !value) return '';

  try {
    const url = new URL(value, document.baseURI);
    return url.protocol === 'https:' ? url.href : '';
  } catch {
    return '';
  }
}

function uniqueItems(items) {
  const seen = new Set();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function refreshLocalSnapshot(current, next) {
  if (!next) return;

  const changed = ['handle', 'title', 'price', 'imageUrl', 'imageAlt'].some(
    (key) => current[key] !== next[key] && next[key],
  );
  if (!changed) return;

  runtime.localItems = runtime.localItems.map((item) =>
    item.id === current.id
      ? Object.freeze({ ...item, ...next, savedAt: item.savedAt })
      : item,
  );
  persistLocalItems();
}

function toggleLocalItem(item, messages = {}) {
  const existing = runtime.localItems.find((localItem) => localItem.id === item.id);
  if (existing) {
    removeLocalItem(item.id, messages.removedMessage);
    return;
  }

  if (runtime.localItems.length >= config.maxLocalItems) {
    announce(config.messages.limitReached);
    return;
  }

  runtime.localItems = [item, ...runtime.localItems];
  const persisted = persistLocalItems();
  notifyStateChange();
  announce(persisted ? messages.addedMessage : config.messages.storageError);
}

function removeLocalItem(productId, message) {
  const nextItems = runtime.localItems.filter((item) => item.id !== productId);
  if (nextItems.length === runtime.localItems.length) return;

  runtime.localItems = nextItems;
  const persisted = persistLocalItems();
  notifyStateChange();
  announce(persisted ? message : config.messages.storageError);
}

function combinedCount() {
  const accountCount = Math.max(runtime.account.count, runtime.account.productIds.size);
  const localOnlyCount = runtime.localItems.filter(
    (item) => !runtime.account.productIds.has(item.id),
  ).length;
  return accountCount + localOnlyCount;
}

function combinedItems() {
  const localIds = new Set(runtime.localItems.map((item) => item.id));
  return [
    ...runtime.localItems.map((item) => ({ item, source: 'local' })),
    ...runtime.account.items
      .filter((item) => !localIds.has(item.id))
      .map((item) => ({ item, source: 'account' })),
  ];
}

function createDrawerItem({ item, source }) {
  const row = document.createElement('li');
  row.className = 'rosarium-wishlist-drawer__item';
  row.dataset.productId = item.id;

  const productUrl = buildProductUrl(item.handle);
  const media = productUrl ? document.createElement('a') : document.createElement('div');
  media.className = 'rosarium-wishlist-drawer__media';
  if (media instanceof HTMLAnchorElement) {
    media.href = productUrl;
    media.setAttribute(
      'aria-label',
      template(config.messages.openProduct, '__PRODUCT__', item.title),
    );
  }

  if (item.imageUrl) {
    const image = document.createElement('img');
    image.src = item.imageUrl;
    image.alt = item.imageAlt;
    image.loading = 'lazy';
    image.width = 88;
    image.height = 88;
    media.append(image);
  } else {
    media.append(createHeartIcon('rosarium-wishlist-drawer__placeholder-icon'));
  }

  const details = document.createElement('div');
  details.className = 'rosarium-wishlist-drawer__details';

  const title = productUrl ? document.createElement('a') : document.createElement('span');
  title.className = 'rosarium-wishlist-drawer__product-title';
  title.textContent = item.title;
  if (title instanceof HTMLAnchorElement) title.href = productUrl;
  details.append(title);

  if (item.price) {
    const price = document.createElement('span');
    price.className = 'rosarium-wishlist-drawer__price';
    price.textContent = template(config.messages.fromPrice, '__PRICE__', item.price);
    details.append(price);
  }

  if (source === 'account') {
    const saved = document.createElement('span');
    saved.className = 'rosarium-wishlist-drawer__saved-status';
    saved.textContent = config.messages.accountSaved;
    details.append(saved);
  }

  if (productUrl) {
    const order = document.createElement('a');
    order.className = 'rosarium-wishlist-drawer__order-action';
    order.href = productUrl;
    order.textContent = config.messages.orderCta;
    order.setAttribute(
      'aria-label',
      template(config.messages.orderProduct, '__PRODUCT__', item.title),
    );
    details.append(order);
  }

  const remove = source === 'local' ? document.createElement('button') : document.createElement('a');
  remove.className = 'rosarium-wishlist-drawer__remove';
  remove.setAttribute(
    'aria-label',
    template(config.messages.removeProduct, '__PRODUCT__', item.title),
  );
  remove.append(createRemoveIcon());

  if (remove instanceof HTMLButtonElement) {
    remove.type = 'button';
    remove.dataset.wishlistRemoveLocal = '';
    remove.dataset.productId = item.id;
    remove.dataset.messageTemplate = config.messages.removedLocal || '';
  } else {
    const removeUrl = buildAccountRoute('remove', [item.id]);
    if (removeUrl) remove.href = removeUrl;
    remove.rel = 'nofollow';
  }

  row.append(media, details, remove);
  return row;
}

function createHeartIcon(className) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 20 20');
  svg.setAttribute('aria-hidden', 'true');
  svg.classList.add(className);
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute(
    'd',
    'M10 17.12 3.14 10.4a4.45 4.45 0 0 1 0-6.36 4.6 4.6 0 0 1 6.43 0l.43.43.43-.43a4.6 4.6 0 0 1 6.43 0 4.45 4.45 0 0 1 0 6.36L10 17.12Z',
  );
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-width', '1.5');
  svg.append(path);
  return svg;
}

function createRemoveIcon() {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 20 20');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M4 4l12 12M16 4 4 16');
  path.setAttribute('fill', 'none');
  path.setAttribute('stroke', 'currentColor');
  path.setAttribute('stroke-linecap', 'square');
  path.setAttribute('stroke-width', '1.5');
  svg.append(path);
  return svg;
}

function buildProductUrl(handle) {
  const normalizedHandle = normalizeHandle(handle);
  if (!normalizedHandle) return '';

  try {
    return new URL(`${config.rootUrl}products/${encodeURIComponent(normalizedHandle)}`, location.origin).href;
  } catch {
    return '';
  }
}

function buildAccountRoute(action, productIds = []) {
  const pageUrl = runtime.account.pageUrl || config.pageUrl;
  if (!pageUrl) return '';

  try {
    const url = new URL(pageUrl, document.baseURI);
    const pagePath = url.pathname.match(/^(.*\/pages\/[^/]+)/)?.[1];
    if (!pagePath) return '';
    if (
      url.searchParams.has(ACCOUNT_ACTION_QUERY_KEY) ||
      url.searchParams.has(ACCOUNT_ITEMS_QUERY_KEY)
    ) {
      return '';
    }

    url.pathname = pagePath;
    let actionValue = '';
    let itemsValue = '';

    if (action === 'remove') {
      const productId = normalizeProductId(productIds[0]);
      if (!productId) return '';
      actionValue = 'remove';
      itemsValue = productId;
    } else if (action === 'import') {
      const ids = [...new Set(productIds.map(normalizeProductId).filter(Boolean))].slice(
        0,
        config.maxLocalItems,
      );
      const payload = ids.join('.');
      if (
        ids.length === 0 ||
        payload.length > MAX_IMPORT_PAYLOAD_LENGTH ||
        !IMPORT_PAYLOAD_PATTERN.test(payload)
      ) {
        return '';
      }
      actionValue = 'import-v1';
      itemsValue = payload;
    }

    if (!actionValue) return url.href;

    const actionQuery = new URLSearchParams({
      [ACCOUNT_ACTION_QUERY_KEY]: actionValue,
      [ACCOUNT_ITEMS_QUERY_KEY]: itemsValue,
    }).toString();

    return `${url.origin}${pagePath}${url.search}${url.search ? '&' : '?'}${actionQuery}${url.hash}`;
  } catch {
    return '';
  }
}

function template(value, token, replacement) {
  return typeof value === 'string' ? value.replace(token, replacement || '') : '';
}

function announce(message) {
  if (!message) return;
  document.querySelectorAll('[data-wishlist-live]').forEach((liveRegion) => {
    liveRegion.textContent = '';
    queueMicrotask(() => {
      liveRegion.textContent = message;
    });
  });
}

function notifyStateChange() {
  document
    .querySelectorAll('rosarium-wishlist-action, rosarium-wishlist-header')
    .forEach((element) => element.sync?.());
  document.querySelectorAll('rosarium-wishlist-drawer').forEach((drawer) => drawer.render?.());
}

function normalizeAccountState(value) {
  const items = uniqueItems(
    (Array.isArray(value?.items) ? value.items : []).map(normalizeItem).filter(Boolean),
  );
  const productIds = new Set(
    [
      ...(Array.isArray(value?.productIds) ? value.productIds : []),
      ...items.map((item) => item.id),
    ]
      .map(normalizeProductId)
      .filter(Boolean),
  );
  const count = Number.isInteger(value?.count) && value.count >= 0
    ? value.count
    : productIds.size;

  return {
    signedIn: value?.signedIn === true,
    count,
    items,
    productIds,
    pageUrl: safeHttpsUrl(value?.pageUrl) || config.pageUrl,
  };
}

function applyAccountState(value) {
  const next = normalizeAccountState(value);
  runtime.signedIn = next.signedIn || config.signedIn;
  runtime.account = next;

  if (next.signedIn && next.productIds.size > 0) {
    const unsyncedItems = runtime.localItems.filter((item) => !next.productIds.has(item.id));
    if (unsyncedItems.length !== runtime.localItems.length) {
      runtime.localItems = unsyncedItems;
      persistLocalItems();
    }
  }

  notifyStateChange();
}

function bootstrap() {
  if (!customElements.get('rosarium-wishlist-action')) {
    customElements.define('rosarium-wishlist-action', RosariumWishlistAction);
  }
  if (!customElements.get('rosarium-wishlist-header')) {
    customElements.define('rosarium-wishlist-header', RosariumWishlistHeader);
  }
  if (!customElements.get('rosarium-wishlist-drawer')) {
    customElements.define('rosarium-wishlist-drawer', RosariumWishlistDrawer);
  }

  const initialAccountState = globalThis.__rosariumWishlistAccountState;
  if (initialAccountState) applyAccountState(initialAccountState);
  notifyStateChange();

  globalThis.render_wishlist_to_text = () => JSON.stringify({
    version: 2,
    mode: document.querySelector('#rosarium-wishlist-dialog')?.open ? 'open' : 'closed',
    signedIn: runtime.signedIn,
    localItemCount: runtime.localItems.length,
    accountItemCount: Math.max(runtime.account.count, runtime.account.productIds.size),
    combinedItemCount: combinedCount(),
    pendingSyncCount: runtime.localItems.length,
    renderedHeartCount: document.querySelectorAll('rosarium-wishlist-action').length,
    persistence: {
      type: 'localStorage',
      schemaVersion: STORAGE_VERSION,
      status: runtime.storageStatus,
    },
    accountPersistence: 'shopify_customer_metafield',
    authentication: 'shopify_customer_accounts',
  });
}

window.addEventListener(ACCOUNT_STATE_EVENT, (event) => applyAccountState(event.detail));
window.addEventListener('storage', (event) => {
  if (event.key !== STORAGE_KEY && event.key !== null) return;
  runtime.localItems = readLocalItems();
  notifyStateChange();
});
window.addEventListener('pageshow', () => {
  runtime.localItems = readLocalItems();
  notifyStateChange();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
} else {
  bootstrap();
}
